// src/redux/slices/pinclueSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { clueData } from "../../data/clueData";
import { loadJSON, saveJSON, isHydrating } from "../../utils/gamePersistence";

const LEVEL_KEYS = ["rookie", "intermediate", "advanced"];

// Always initialize from LS (server snapshot may be restored before store wiring finishes)
const initialState = loadJSON("clueState", null) ?? { missions: clueData };

// --- helpers ---
const getMission = (state, missionId) =>
  state.missions.find((m) => m.id === missionId) || null;

const findClue = (mission, clueId, level) => {
  if (!mission || !mission.coins) return null;

  // If a level is provided, search only that level first
  if (level && mission.coins[level]) {
    const c = mission.coins[level].find((cl) => cl.id === clueId);
    if (c) return c;
  }

  // Otherwise scan all levels in order
  for (const key of LEVEL_KEYS) {
    const list = mission.coins[key];
    if (!Array.isArray(list)) continue;
    const c = list.find((cl) => cl.id === clueId);
    if (c) return c;
  }
  return null;
};

const visitAllClues = (mission, cb) => {
  if (!mission?.coins) return;
  for (const key of LEVEL_KEYS) {
    const list = mission.coins[key];
    if (!Array.isArray(list)) continue;
    list.forEach(cb);
  }
};

// Persist ONLY when not hydrating (prevents clobbering server snapshot on restore)
const persist = (state) => {
  if (!isHydrating()) {
    saveJSON("clueState", state);
  }
};

const clueSlice = createSlice({
  name: "pinclue",
  initialState,
  reducers: {
    /** Replace the whole clue state from LS/server snapshot */
    replaceClueState(_state, action) {
      const next = action.payload;
      return next && typeof next === "object" ? next : _state;
    },

    /**
     * Payload:
     *   { missionId, clueId, level? }
     * level is optional (rookie|intermediate|advanced). If omitted, first match wins.
     */
    pinClue(state, action) {
      const { missionId, clueId, level } = action.payload || {};
      const mission = getMission(state, missionId);
      if (!mission) return;

      const clue = findClue(mission, clueId, level);
      if (!clue || clue.pinned) return;

      clue.pinned = true;
      persist(state);
    },

    /**
     * Backward-compatible bulk pin.
     * Accepts either:
     *   { missionId, clueIds: string[] }  // legacy
     * OR
     *   { missionId, clues: Array<string | { clueId: string, level?: string }>}
     */
    pinMultipleClues(state, action) {
      const { missionId } = action.payload || {};
      const mission = getMission(state, missionId);
      if (!mission) return;

      const items =
        action.payload?.clues ??
        (Array.isArray(action.payload?.clueIds) ? action.payload.clueIds : []);

      items.forEach((entry) => {
        const clueId = typeof entry === "string" ? entry : entry?.clueId;
        const level = typeof entry === "string" ? undefined : entry?.level;
        if (!clueId) return;

        const clue = findClue(mission, clueId, level);
        if (!clue || clue.pinned) return;
        clue.pinned = true;
      });

      persist(state);
    },

    /**
     * Payload:
     *   { missionId, clueId, level? }
     * If level omitted, first match is unpinned.
     */
    unpinClue(state, action) {
      const { missionId, clueId, level } = action.payload || {};
      const mission = getMission(state, missionId);
      if (!mission) return;

      const clue = findClue(mission, clueId, level);
      if (!clue) return;

      clue.pinned = false;
      // If you want to reset draggable placement when unpinned, keep this:
      // clue.position = null;

      persist(state);
    },

    /**
     * Clears all pins across all missions and levels.
     */
    clearAllPins(state) {
      state.missions.forEach((mission) => {
        visitAllClues(mission, (clue) => {
          clue.pinned = false;
          // Optional: also clear transient placement if you use it
          // clue.position = null;
        });
      });
      persist(state);
    },
  },
});

export const {
  replaceClueState,
  pinClue,
  pinMultipleClues,
  unpinClue,
  clearAllPins,
} = clueSlice.actions;

export default clueSlice.reducer;
